// PAUSE A PERSON'S LOGIN, TEMPORARILY (owner, 2026-10-15: "add option in
// users and permissions, to disable user account temporarly, could be done
// in bluk with cehckboxes, once check box is unchecked user can logina
// gain").
//
// Three pieces for the Users and permissions page:
//   LoginCell         the "Login" column: ticked = can sign in. Unticking
//                     asks first (naming the person), re-ticking restores.
//                     A paused person also carries a red "Paused" tag whose
//                     tooltip says who paused them and when.
//   BulkLoginButtons  "Pause login" / "Restore login" for the ticked rows.
//   useLoginPause     the confirm, the call, and the per-person outcome.
//
// The server decides who may pause whom (back/utils/loginSuspension) and
// answers per person. When some were refused, the page SAYS which and why
// in a dialog - a batch that partly failed must not look finished.
//
// Not the same as Delete / Disable (permanent, work handed over): a paused
// person keeps their roster places, assignments and permissions.
import React, { useCallback, useState } from "react";
import { Button, Checkbox, Modal, Tag, Tooltip } from "antd";
import { setLoginSuspension } from "api/users/loginSuspension";
import addNotification from "helpers/notify";

export const isPaused = (u) => !!(u && u.loginSuspended && u.loginSuspended.at);

export const personName = (u) =>
  [u?.name, u?.lastName].filter(Boolean).join(" ").trim() || u?.email || "this person";

export function pausedTitle(u) {
  const s = u?.loginSuspended;
  if (!s?.at) return "";
  const when = new Date(s.at);
  const date = Number.isNaN(when.getTime()) ? "" : when.toLocaleString();
  return `Login paused${s.byName ? ` by ${s.byName}` : ""}${date ? ` on ${date}` : ""}`;
}

const SELF_WHY = "You cannot pause your own login";
const NO_RIGHT_WHY = "Only a company admin, or someone allowed to edit users, can pause a login";

export function LoginCell({ user, isSelf, canEdit, busy, onToggle }) {
  const paused = isPaused(user);
  const why = isSelf
    ? SELF_WHY
    : !canEdit
      ? NO_RIGHT_WHY
      : paused
        ? `${pausedTitle(user)}. Tick to let them sign in again.`
        : "Can sign in. Untick to pause their login.";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Tooltip title={why}>
        <span>
          <Checkbox
            data-testid={`login-cell-${user?._id}`}
            checked={!paused}
            disabled={!!isSelf || !canEdit || !!busy}
            onChange={(e) => onToggle && onToggle(user, !e.target.checked)}
          />
        </span>
      </Tooltip>
      {paused && (
        <Tooltip title={pausedTitle(user)}>
          <Tag color="red" style={{ marginInlineEnd: 0 }}>Paused</Tag>
        </Tooltip>
      )}
    </span>
  );
}

export function BulkLoginButtons({ selected, canEdit, busy, onRequest }) {
  if (!canEdit || !selected?.length) return null;
  const toPause = selected.filter((u) => !isPaused(u));
  const toRestore = selected.filter((u) => isPaused(u));
  return (
    <span style={{ display: "inline-flex", gap: 8, marginRight: 8 }}>
      <Button size="small" danger disabled={!toPause.length || busy} onClick={() => onRequest(toPause, true)}>
        Pause login{toPause.length ? ` (${toPause.length})` : ""}
      </Button>
      <Button size="small" disabled={!toRestore.length || busy} onClick={() => onRequest(toRestore, false)}>
        Restore login{toRestore.length ? ` (${toRestore.length})` : ""}
      </Button>
    </span>
  );
}

const listNames = (people) => {
  const names = people.map(personName);
  if (names.length <= 8) return names;
  return [...names.slice(0, 8), `and ${names.length - 8} more`];
};

// setUsers: the page's list setter; the rows the server changed are patched
// in place so the column and tag update without a reload.
export function useLoginPause({ setUsers }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { people }
  const [outcome, setOutcome] = useState(null); // response with refusals

  const run = useCallback(async (people, suspended) => {
    setBusy(true);
    try {
      const res = await setLoginSuspension(people.map((p) => p._id), suspended);
      const done = new Map((res?.results || []).filter((r) => r.status === "done").map((r) => [String(r.userId), r]));
      if (done.size && setUsers) {
        setUsers((prev) => (prev || []).map((u) => {
          const r = done.get(String(u._id));
          if (!r) return u;
          return { ...u, loginSuspended: suspended ? (r.loginSuspended || { at: new Date().toISOString() }) : null };
        }));
      }
      if (res?.refused) {
        setOutcome({ ...res, suspended });
      } else {
        const verb = suspended ? "paused" : "restored";
        const n = res?.done || 0;
        addNotification(
          n
            ? `Login ${verb} for ${n} ${n === 1 ? "person" : "people"}${res?.unchanged ? ` (${res.unchanged} already ${suspended ? "paused" : "able to sign in"})` : ""}`
            : `Nothing changed: ${suspended ? "already paused" : "already able to sign in"}`,
          "success"
        );
      }
    } catch (e) {
      addNotification(e?.err || e?.message || "Could not change sign-in for those people", "danger");
    } finally {
      setBusy(false);
    }
  }, [setUsers]);

  // Pausing asks first and names the people; restoring is immediate.
  const request = useCallback((people, suspended) => {
    const list = (people || []).filter(Boolean);
    if (!list.length) return;
    if (suspended) setConfirm({ people: list });
    else run(list, false);
  }, [run]);

  const element = (
    <>
      <Modal
        open={!!confirm}
        title={confirm && confirm.people.length === 1 ? `Pause login for ${personName(confirm.people[0])}?` : `Pause login for ${confirm?.people.length || 0} people?`}
        okText="Pause login"
        okButtonProps={{ danger: true, "data-testid": "login-pause-ok" }}
        cancelText="Cancel"
        onCancel={() => setConfirm(null)}
        onOk={() => { const p = confirm.people; setConfirm(null); run(p, true); }}
        destroyOnClose
      >
        <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
          {confirm && listNames(confirm.people).map((n, i) => <li key={i}>{n}</li>)}
        </ul>
        <div style={{ fontSize: 13, color: "#555" }}>
          They will not be able to sign in, and anyone signed in now is signed out on their next click.
          Nothing else changes: they keep their projects, assignments and permissions, and stay in every list.
          Tick their Login box again to let them back in.
        </div>
      </Modal>
      <Modal
        open={!!outcome}
        title={outcome ? `${outcome.done} ${outcome.suspended ? "paused" : "restored"}, ${outcome.refused} not changed` : ""}
        onCancel={() => setOutcome(null)}
        footer={<Button onClick={() => setOutcome(null)}>Close</Button>}
      >
        <ul style={{ paddingLeft: 18 }} data-testid="login-pause-refusals">
          {(outcome?.results || []).filter((r) => r.status === "refused").map((r) => (
            <li key={r.userId}><b>{r.name || r.email || "Unknown account"}</b>: {r.reason}</li>
          ))}
        </ul>
      </Modal>
    </>
  );

  return { request, busy, element };
}
